import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { deleteStudio, updateStudioInfo } from '../../../api/studio/info';
import { getLocalTime, validateNameLength } from '../../../lib';
import { messageError, messageSuccess } from '../../../components/message';
import {
  GameSettingInfoLayout,
  IGameSettingItemInfo,
} from '../game-setting-info-layout';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { StudioAction, getStudioList } from '../../../store/modules/studio';
import { GamesAction } from '../../../store/modules/games';
import { EditInput } from '../game-setting-info-layout/edit-input';
import { DoubleModal } from '../../../components/double-modal';
import { IComponentProps } from '../../../model/studio';
import { splitEmail } from '../../utils';
// import { getStudioMembers } from '../../../api';
import {
  IAPIStudioMemberHasPermissionItem,
  IAuditItem,
  RoleType,
} from '../../../model/api-modules';
import getFormatMessage from '../../../locales/get-format-message';
import { getStudioMembersHasPermission } from '../../../api';
import { CompanyAuth } from '../company';
import classNames from 'classnames';
import { PermissionCheck } from '../../component/permission-check';
import { RbacRoleObjectKey } from '../../../model';
import { IMidasAccountInfo } from '../../../model/billing-center';
import { auditWriteLog } from '../../lib/aduit-log';
import { IRootState } from '../../../store/reducers';

interface IProps extends IComponentProps {
  readonly accountInfo?: IMidasAccountInfo | null;
}

export const SettingContentComponent = (props: IProps) => {
  const history = useHistory();

  const [showNameEdit, setShowNameEdit] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const studioName = props.studioInfo.studio.Name;
  const studioID = props.studioInfo.studio.StudioID;
  const { companyInfo, accountInfo } = props;
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  const studioDispatch = useDispatch<Dispatch<StudioAction | GamesAction>>();
  const [memberOwner, setMemberOwner] =
    useState<IAPIStudioMemberHasPermissionItem>();
  const onDeleteStudio = async () => {
    try {
      const res = await deleteStudio(studioID);
      auditWriteLog(IAuditItem.DeleteStudio, {
        detail: {
          studioID,
        },
      });
      if (res && res.code === 0) {
        messageSuccess(
          getFormatMessage('studio_setting_delete_success', {
            params: {
              studioName,
            },
          })
        );
        history.replace('/studio/select-studio');
      }
    } catch (err: any) {
      if (err?.apiError?.code === 101235) {
        return false;
      }
      messageError(
        getFormatMessage('studio_setting_delete_failed', {
          params: {
            studioName,
          },
        })
      );
      setDeleteModal(false);
    }
    return true;
  };
  const queryStudioOwner = async () => {
    // const res = await getStudioMembers(studioID);
    const res = await getStudioMembersHasPermission(studioID);
    if (res) {
      const ownerItem = res.find((item) =>
        item.RbacMember?.some((member) => member.roleType === RoleType.Owner)
      );
      if (ownerItem) {
        setMemberOwner(ownerItem);
      }
    }
  };

  useEffect(() => {
    queryStudioOwner();
  }, []);

  const putStudioName = async (value: string) => {
    if (value === studioName) {
      setShowNameEdit(false);
      return true;
    }

    const res = await updateStudioInfo(studioID, {
      Name: value,
    });
    auditWriteLog(IAuditItem.EditStudio, {
      detail: {
        Name: value,
      },
    });
    if (res) {
      await getStudioList(studioDispatch)();
      setShowNameEdit(false);
      await props.onReload();
      messageSuccess(getFormatMessage('studio_setting_put_name_success'));
      return true;
    }
    messageError(getFormatMessage('studio_setting_put_name_failed'));
    return false;
  };

  const renderDeleteModal = () => {
    if (deleteModal) {
      return (
        <DoubleModal
          onClose={() => setDeleteModal(false)}
          title={getFormatMessage('delete_studio')}
          description={getFormatMessage('delete_studio_confirm_desc', {
            params: {
              studioName,
            },
          })}
          okText={getFormatMessage('delete_studio')}
          onSubmit={onDeleteStudio}
          failedConfig={{
            description: getFormatMessage('delete_studio_failed_desc'),
            title: getFormatMessage('delete_studio_failed'),
          }}
        />
      );
    }
    return null;
  };

  const renderStudioName = () => {
    if (showNameEdit) {
      return (
        <EditInput
          value={studioName}
          validator={validateNameLength(getFormatMessage('studio_name'))}
          onSubmit={putStudioName}
        />
      );
    }
    return props.studioInfo.studio.Name;
  };

  const renderDeleteIcon = () => {
    const buttonIcon = (
      <div
        className={classNames(
          'delete-button',
          !props.isOwner ? 'disabled' : ''
        )}
        onClick={() => {
          if (!props.isOwner) {
            return;
          }
          setDeleteModal(true);
        }}
      >
        <span className="delete-text">{getFormatMessage('delete_studio')}</span>
      </div>
    );

    return buttonIcon;
  };

  const renderFooterText = () => {
    const deleteClassName = classNames('delete-button-box');
    return (
      <div className="studio-setting-footer-text">
        <div className="text remove-text">
          {getFormatMessage('delete_studio_permanently_tip')}
        </div>
        <PermissionCheck permissionKey={RbacRoleObjectKey.StudioDelete}>
          <div className={deleteClassName}>{renderDeleteIcon()}</div>
        </PermissionCheck>
      </div>
    );
  };

  const infoItems: ReadonlyArray<IGameSettingItemInfo> = [
    {
      left: getFormatMessage('studio_name_uppercase'),
      right: renderStudioName(),
      editConfig: {
        disabled: !props.isOwner,
        editing: showNameEdit,
        onEdit: () => {
          setShowNameEdit(true);
        },
      },
    },
    {
      left: getFormatMessage('studio_id'),
      right: studioID,
      copyConfig: {
        disabled: false,
        text: studioID,
      },
    },
    {
      left: getFormatMessage('owner_email'),
      right: `${memberOwner?.UserName || splitEmail(memberOwner?.UserID)} (${
        memberOwner?.UserID
      })`,
    },
    {
      left: getFormatMessage('create_time_local'),
      right: getLocalTime(
        props.studioInfo.studio.CreateTime,
        userInfo?.UserSettings?.Timezone
      ),
    },
  ];

  return (
    <>
      <GameSettingInfoLayout items={infoItems} />

      <CompanyAuth
        companyInfo={companyInfo}
        accountInfo={accountInfo}
        onReload={props.onReload}
      />

      {renderDeleteModal()}
      {renderFooterText()}
    </>
  );
};
